/**
 * The blog, as files in this repo.
 *
 * WHY NOT WORDPRESS. What this replaces was a reverse proxy to a Pantheon WordPress
 * install: `src/api/lib/blog-proxy.ts` fetched someone else's HTML, rewrote its URLs, and
 * wrapped it in a New Leaf shell. That meant our marketing site's uptime depended on a
 * second CMS, the content lived somewhere git could not see it, and the posts that came
 * out of it were generated SEO filler. Several sold commercial, Airbnb and
 * post-construction work, which New Leaf stopped offering in April 2026, so the blog was
 * actively contradicting the rest of the site.
 *
 * Posts are markdown files under `content/blog/`. They are bundled at build time by Vite,
 * not read from disk at request time, because a Worker has no filesystem. That is the
 * whole trick: `import.meta.glob(..., { eager: true })` turns the directory into a module
 * graph entry, so a post is as fast to serve as a hardcoded string and the sitemap
 * generator can read the same files from Node.
 *
 * DEPENDENCIES ARE DELIBERATELY MINIMAL. The frontmatter parser below is about twenty
 * lines instead of `gray-matter`, because this build is already fragile about bundling
 * (see the `@cloudflare/vite-plugin` note in the sibling commercial-website repo) and a
 * YAML engine is a lot of surface area for "read six string fields".
 */

import { marked } from 'marked';

export interface Post {
  slug: string;
  title: string;
  /** Meta description AND the card blurb. One sentence, written, not truncated. */
  description: string;
  /** ISO date. Drives ordering and the Article JSON-LD. */
  date: string;
  updated?: string;
  /** One of the pillars in CONTENT-PLAN.md. Drives the index filter. */
  category: string;
  tags: string[];
  /** Minutes, computed — never hand-set, so it cannot drift from the body. */
  readingMinutes: number;
  html: string;
  /** Raw markdown body, for anything that needs the text rather than the render. */
  markdown: string;
}

interface Frontmatter {
  [key: string]: string | string[];
}

/**
 * Parse the `---` block at the top of a post.
 *
 * Supports exactly what the posts use: `key: value` and `key: [a, b, c]`. Quotes around
 * a value are optional and stripped. Anything else is a mistake in the post, and it
 * should be loud rather than silently dropped, so unknown shapes throw at build time.
 */
function parseFrontmatter(raw: string): { data: Frontmatter; body: string } {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  if (!match) {
    throw new Error('Post is missing its --- frontmatter block');
  }
  const data: Frontmatter = {};
  for (const line of match[1].split(/\r?\n/)) {
    if (!line.trim() || line.trimStart().startsWith('#')) continue;
    const sep = line.indexOf(':');
    if (sep === -1) throw new Error(`Unparseable frontmatter line: ${line}`);
    const key = line.slice(0, sep).trim();
    let value = line.slice(sep + 1).trim();
    if (value.startsWith('[') && value.endsWith(']')) {
      data[key] = value
        .slice(1, -1)
        .split(',')
        .map((v) => v.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean);
    } else {
      data[key] = value.replace(/^["']|["']$/g, '');
    }
  }
  return { data, body: match[2] };
}

function required(data: Frontmatter, key: string, slug: string): string {
  const v = data[key];
  if (typeof v !== 'string' || !v) {
    throw new Error(`Post "${slug}" is missing required frontmatter: ${key}`);
  }
  return v;
}

/**
 * 200 wpm is the usual reading-speed constant and it is too fast for anything with
 * instructions in it. 180 is closer to how someone actually reads a how-to on a phone
 * while standing in the room they are about to clean.
 */
function readingTime(markdown: string): number {
  const words = markdown.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 180));
}

marked.setOptions({ gfm: true, breaks: false });

const files = import.meta.glob('/content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function build(): Post[] {
  const posts = Object.entries(files).map(([path, raw]) => {
    const slug = path.split('/').pop()!.replace(/\.md$/, '');
    const { data, body } = parseFrontmatter(raw);
    return {
      slug,
      title: required(data, 'title', slug),
      description: required(data, 'description', slug),
      date: required(data, 'date', slug),
      updated: typeof data.updated === 'string' ? data.updated : undefined,
      category: required(data, 'category', slug),
      tags: Array.isArray(data.tags) ? data.tags : [],
      readingMinutes: readingTime(body),
      html: marked.parse(body) as string,
      markdown: body,
    };
  });
  // Newest first. Ties broken by title so ordering is stable across builds rather than
  // depending on whatever order the glob happened to return.
  return posts.sort((a, b) => b.date.localeCompare(a.date) || a.title.localeCompare(b.title));
}

export const posts: Post[] = build();

export function getPost(slug: string): Post | undefined {
  return posts.find((p) => p.slug === slug);
}

export function categories(): string[] {
  return [...new Set(posts.map((p) => p.category))].sort();
}

/**
 * Related posts: same category first, then shared tags, never itself. Deliberately not a
 * similarity score. With a few dozen posts a scoring function is worse than the obvious
 * rule and much harder to explain when it picks something odd.
 */
export function related(post: Post, limit = 3): Post[] {
  const scored = posts
    .filter((p) => p.slug !== post.slug)
    .map((p) => ({
      p,
      score:
        (p.category === post.category ? 2 : 0) +
        p.tags.filter((t) => post.tags.includes(t)).length,
    }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || b.p.date.localeCompare(a.p.date));
  return scored.slice(0, limit).map((s) => s.p);
}
